
import { baseUrl } from "@/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Axios from "axios";
import crypto from 'crypto';
import { useEffect, useState } from "react";
import { useUserContext } from "./UserContext";
import { decrypt, encrypt, getMyPrivateKey, getMyPublicKey, setMyPrivateKey, setMyPublicKey } from "./api";

const KeysComponent = () => {
  const queryClient = useQueryClient();
  const { user, setUser } = useUserContext();
  const userId = user?.id;
  const [fetchKeys, setFetchKeys] = useState(false);
  const [publicKeyValue, setPublicKeyValue] = useState<null | string>(null);
  const [privateKeyValue, setPrivateKeyValue] = useState<null | string>(null);
  const privateKeyIdle = !(userId && fetchKeys);
  const { data: privateKeyData, error: privateKeyError, isLoading: privateKeyLoading, refetch: privateKeyRefetch } = useQuery(
    {
      queryKey: ["private-key", userId],
      queryFn: () => getMyPrivateKey(userId),
      enabled: !privateKeyIdle
    }

  );
  const publicKeyIdle = !(userId && fetchKeys)
  const { data: publicKeyData, error: publicKeyError, isLoading: publicKeyLoading, refetch: publicKeyRefetch } = useQuery(
    {
      queryKey: ["public-key", userId],
      queryFn: () => getMyPublicKey(userId),
      enabled: !publicKeyIdle
    }
  );

  const { mutate: publicKeyMutate } = useMutation({
    mutationFn: (key: string) => setMyPublicKey(userId, key)
  });

  const { mutate: privateKeyMutate } = useMutation({
    mutationFn: (key: string) => setMyPrivateKey(userId, key)
  });


  const updateUser = async (privateKey: string, publicKey: string) => {
    const ww = crypto.createECDH('secp256k1');
    ww.setPrivateKey(privateKey, 'base64');
    const encryptionKey = ww.computeSecret(publicKey, 'base64', 'hex');

    if (user?.encrypted) {
      if (!user?.dataDecryptedLocally) {
        const nationalId = decrypt(encryptionKey, user?.nationalId) || "";
        const fullName = decrypt(encryptionKey, user.fullName) || "";
        const fullNameEn = decrypt(encryptionKey, user.fullNameEn) || "";

        const date_of_birth = decrypt(encryptionKey, user.date_of_birth) || "";

        setUser(user ? {
          ...user,
          dataDecryptedLocally: true,
          fullName,
          fullNameEn,
          nationalId,
          date_of_birth,
        } : null);

      }
    } else {
      const nationalIdVerified = user?.nationalIdVerified;
      if (nationalIdVerified && !user.encrypted) {
        const nationalId = encrypt(encryptionKey, user?.nationalId);
        const fullNameEn = encrypt(encryptionKey, user.fullNameEn);
        const fullName = encrypt(encryptionKey, user.fullName);
        const date_of_birth = encrypt(encryptionKey, user.date_of_birth);
        const requestBody: any = {
          nationalId,
          fullName,
          fullNameEn,
          date_of_birth
        }

        if (user.age) {
          const age = encrypt(encryptionKey, `${user.age}`);
          requestBody.age = age;
        }

        await Axios.post(`${baseUrl}api/patients/${user.id}/encrypt`, requestBody);

        setUser(user ? {
          ...user,
          dataDecryptedLocally: true,
          encrypted: true
        } : null);
      }

    }
  }

  useEffect(() => {
    if (!userId) {
      queryClient.removeQueries({
        queryKey: ["private-key", userId]
      });
      queryClient.removeQueries({
        queryKey: ["public-key", userId]
      });
      setPublicKeyValue(null);
      setPrivateKeyValue(null);
      return;
    }
    const privateKey = localStorage.getItem('@privateKey');
    const publicKey = localStorage.getItem('@pubKey');
    if (!privateKey || !publicKey) {
      setFetchKeys(true);
    } else {
      setPrivateKeyValue(privateKey)
      setPublicKeyValue(publicKey);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    if (publicKeyIdle) return;
    if (publicKeyLoading) return;
    if (publicKeyData) {
      localStorage.setItem('@pubKey', publicKeyData);
      setPublicKeyValue(publicKeyData);
      return;
    }
    if (publicKeyError) {
      const ecdh = crypto.createECDH('secp256k1');
      ecdh.generateKeys();
      const newPublicKey = ecdh.getPublicKey().toString('base64');
      publicKeyMutate(newPublicKey, {
        onSuccess: () => {
          publicKeyRefetch();
          // AsyncStorage.setItem('@pubKey', newPublicKey);
          // setPublicKeyValue(newPublicKey);
        }
      });
    }
  }, [userId, publicKeyData, publicKeyIdle, publicKeyLoading, publicKeyError]);

  useEffect(() => {
    if (!userId) return;
    if (privateKeyIdle) return;
    if (privateKeyLoading) return;

    if (privateKeyData) {
      localStorage.setItem('@privateKey', privateKeyData);
      setPrivateKeyValue(privateKeyData);
      return;
    }
    if (privateKeyError) {
      const ecdh = crypto.createECDH('secp256k1');
      ecdh.generateKeys();
      const newPrivateKey = ecdh.getPrivateKey().toString('base64');
      privateKeyMutate(newPrivateKey, {
        onSuccess: () => {
          privateKeyRefetch();
          // AsyncStorage.setItem('@privateKey', newPrivateKey);
          // setPrivateKeyValue(newPrivateKey);
        }
      });
    }

  }, [userId, privateKeyData, privateKeyIdle, , privateKeyLoading, privateKeyError]);

  useEffect(() => {
    if (!privateKeyValue || !publicKeyValue) return;
    updateUser(privateKeyValue, publicKeyValue);
  }, [privateKeyValue, publicKeyValue]);

  return null
}

export default KeysComponent;